import { useState } from "react";
import {
  FieldError,
  FieldValues,
  Path,
  UseFormRegister,
} from "react-hook-form";
import { ErrorMessageComponent } from "../ErrorMessage/ErrorMessage";
import PasswordMaskComponent from "./PasswordMask";

interface ReactHookProps<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  register: UseFormRegister<TFormValues>;
}

interface PasswordFieldProps<TFormValues extends FieldValues>
  extends ReactHookProps<TFormValues> {
  disabled: boolean;
  fieldError: FieldError | undefined;
  showCheckboxes?: boolean;
  componentId?: string;
}

interface PasswordCriteria {
  minLength: boolean;
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasSpecialChar: boolean;
  hasNumber: boolean;
}

const PasswordFieldComponent = <TFormValues extends Record<string, unknown>>({
  fieldError,
  disabled,
  showCheckboxes,
  componentId,
  register,
  name,
}: PasswordFieldProps<TFormValues>) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [, setPassword] = useState<string>("");
  const [criteria, setCriteria] = useState<PasswordCriteria>({
    minLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasSpecialChar: false,
    hasNumber: false,
  });

  const handlePasswordChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const newPassword = e.currentTarget.value;
    setPassword(newPassword);
    setCriteria({
      minLength: newPassword.length >= 8,
      hasLowercase: /[a-z]/.test(newPassword),
      hasUppercase: /[A-Z]/.test(newPassword),
      hasSpecialChar: /[\ !\"#$%&'()*+,\-./:;<=>?@[\]^_`{|}~]/.test(
        newPassword
      ),
      hasNumber: /\d/.test(newPassword),
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <div className="relative">
        <input
          id={componentId + "-input-password"}
          type={showPassword ? "text" : "password"}
          className={`fp-text-input static pr-28 sm:pr-[136px] ${fieldError && `border-red-700`}`}
          placeholder="Password"
          autoComplete="off"
          disabled={disabled}
          onKeyUp={handlePasswordChange}
          {...(register && register(name))}
        />
        <div
          className="absolute top-1/2 right-2 text-xs mr-2 transform -translate-y-1/2 border-none bg-transparent cursor-pointer"
          onClick={togglePasswordVisibility}
        >
          <PasswordMaskComponent
            componentId={componentId + "-mask"}
            showPassword={showPassword}
          />
        </div>
      </div>

      {fieldError && (
        <ErrorMessageComponent
          componentId={componentId + "-input-password"}
          fieldError={fieldError}
        />
      )}

      {showCheckboxes && (
        <div className="mt-5 bg-gray-50 rounded-md p-3 grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
          <div
            id={componentId + "-password-criteria-1"}
            className="flex align-middle"
          >
            <input
              id={componentId + "-password-criteria-1-checkbox"}
              className="mr-2 "
              type="checkbox"
              checked={criteria.minLength}
              readOnly
            />
            Minimum 8 characters
          </div>
          <div
            id={componentId + "-password-criteria-2"}
            className="flex align-middle"
          >
            <input
              id={componentId + "-password-criteria-2-checkbox"}
              className="mr-2"
              type="checkbox"
              checked={criteria.hasUppercase}
              readOnly
            />
            One uppercase letter
          </div>
          <div
            id={componentId + "-password-criteria-3"}
            className="flex align-middle"
          >
            <input
              id={componentId + "-password-criteria-3-checkbox"}
              className="mr-2"
              type="checkbox"
              checked={criteria.hasLowercase}
              readOnly
            />
            One lowercase letter
          </div>
          <div
            id={componentId + "-password-criteria-4"}
            className="flex align-middle"
          >
            <input
              id={componentId + "-password-criteria-4-checkbox"}
              className="mr-2"
              type="checkbox"
              checked={criteria.hasNumber}
              readOnly
            />
            One number
          </div>
          <div
            id={componentId + "-password-criteria-5"}
            className="flex align-middle"
          >
            <input
              id={componentId + "-password-criteria-5-checkbox"}
              className="mr-2"
              type="checkbox"
              checked={criteria.hasSpecialChar}
              readOnly
            />
            One special character
          </div>
        </div>
      )}
    </>
  );
};

export default PasswordFieldComponent;
